import { useState } from "react";
import { Link } from "react-router-dom";
import { MutationError, latestError, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import {
  useApproveApproval,
  useAwaitingMe,
  useMyApprovals,
  useRejectApproval,
} from "../recommendation/use-approvals";
import { type Approval, type ApprovalStatus } from "../recommendation/types";

// AC6 — the approver inbox over GA1's four-eyes reads. TWO honest lists in one
// surface (advisor-console handoff §Kabuk):
//   • "Bana onay bekleyenler" (awaiting-me) — PENDING approvals THIS caller may
//     grant; approve/reject run through the governance client. The API returns
//     only what the principal is authorised to grant, never their own request
//     (approver ≠ requester, enforced server-side).
//   • "Gönderdiklerim" (sent-by-me) — approvals the caller REQUESTED, read-only,
//     with their current state (pending/approved/rejected). Over GA1's `mine`
//     read (GET /v1/approvals/mine → useMyApprovals).
// The tab strip is presentation only (our qf-seg segmented control); every call
// is still enforced by the API.
type InboxTab = "awaiting" | "sent";

export function ApproverInbox() {
  const { t } = useT();
  const [tab, setTab] = useState<InboxTab>("awaiting");

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("inbox.heading")}</h1>
        </div>
      </header>

      <div
        className="qf-seg"
        role="tablist"
        aria-label={t("inbox.tabs.label")}
        data-testid="inbox-tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "awaiting"}
          className={`qf-seg__btn${tab === "awaiting" ? " qf-seg__btn--active" : ""}`}
          data-testid="inbox-tab-awaiting"
          onClick={() => setTab("awaiting")}
        >
          <Icon name="tasks" size={15} />
          {t("inbox.tab.awaiting")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "sent"}
          className={`qf-seg__btn${tab === "sent" ? " qf-seg__btn--active" : ""}`}
          data-testid="inbox-tab-sent"
          onClick={() => setTab("sent")}
        >
          <Icon name="records" size={15} />
          {t("inbox.tab.sent")}
        </button>
      </div>

      {tab === "awaiting" ? <AwaitingList /> : <SentList />}
    </section>
  );
}

/** "Bana onay bekleyenler" — the approver's actionable inbox (approve/reject). */
function AwaitingList() {
  const { t } = useT();
  const { data, isLoading, error } = useAwaitingMe();

  return (
    <div role="tabpanel" data-testid="inbox-panel-awaiting">
      <ListState isLoading={isLoading} error={error} />
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="check-circle" size={26} />
          </div>
          <p className="k-empty__title">{t("inbox.none")}</p>
        </div>
      )}

      <ul className="inbox-list">
        {(data ?? []).map((a) => (
          <InboxRow key={a.id} approval={a} />
        ))}
      </ul>
    </div>
  );
}

/** "Gönderdiklerim" — the caller's own requested approvals + their state,
 *  read-only (the requester never acts on their own request). */
function SentList() {
  const { t } = useT();
  const { data, isLoading, error } = useMyApprovals();

  return (
    <div role="tabpanel" data-testid="inbox-panel-sent">
      <ListState isLoading={isLoading} error={error} />
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="k-empty">
          <div className="k-empty__icon">
            <Icon name="records" size={26} />
          </div>
          <p className="k-empty__title">{t("inbox.sent.none")}</p>
        </div>
      )}

      <ul className="inbox-list">
        {(data ?? []).map((a) => (
          <SentRow key={a.id} approval={a} />
        ))}
      </ul>
    </div>
  );
}

function ListState({ isLoading, error }: { isLoading: boolean; error: unknown }) {
  const { t } = useT();
  return (
    <>
      {isLoading && (
        <div className="k-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("inbox.loading")}</span>
          </div>
        </div>
      )}
      {error != null && (
        <div className="k-note k-note--error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <p role="alert" className="k-note__desc">
              {error instanceof ProblemError ? error.problem.detail : t("inbox.heading")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function InboxRow({ approval }: { approval: Approval }) {
  const { t } = useT();
  const approve = useApproveApproval();
  const reject = useRejectApproval();
  const busy = approve.isPending || reject.isPending;
  // Reject asks for a reason. It used to ask via globalThis.prompt?.(), which
  // returns undefined where prompt is unavailable — the handler then returned and
  // the button was simply inert, with nothing on screen. Inline, two-step, like
  // the consent withdrawal in household-360.
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  // A7 — the SERVER says where to review (or that there is nowhere). The console
  // no longer hardcodes "Tier-2 only": that condition was never about Tier-2
  // being special, it was the only mapping anyone had wired.
  const review = approval.review?.app === "console" ? approval.review.path : null;

  return (
    <li
      className="k-card k-card--pad-sm inbox-row"
      data-testid="inbox-row"
      data-action={approval.action}
    >
      <div className="inbox-row__body">
        {/* Governance kept legible + calm: the action reads as a review-blue pill. */}
        <span className="k-status k-status--review">
          <span className="k-status__dot" />
          {approval.action}
        </span>
        <span className="inbox-row__meta">
          {t("inbox.resource")} <code>{approval.resourceId?.slice(0, 8) ?? "—"}</code> ·{" "}
          {t("inbox.requester")} <strong>{approval.requestedBy}</strong>
        </span>
      </div>
      <span className="inbox-actions">
        {/* Review-in-context so the approver sees the act before granting it. */}
        {review ? (
          <Link className="k-btn k-btn--ghost k-btn--sm" to={review} data-testid="inbox-review">
            <Icon name="arrow-right" size={16} />
            {t("inbox.review")}
          </Link>
        ) : (
          /* SAY IT. A missing link used to render as empty space, which reads as
             "nothing to see" rather than "you cannot see it". Naming the absence
             is what lets an approver decline to sign for something they were
             never shown — the whole reason four-eyes exists. */
          <span className="inbox-noreview" data-testid="inbox-no-review" role="note">
            <Icon name="alert" size={14} />
            {t("inbox.noReview")}
          </span>
        )}
        <button
          type="button"
          className="k-btn k-btn--approve k-btn--sm"
          disabled={busy}
          onClick={() => approve.mutate(approval.id)}
        >
          {t("inbox.approve")}
        </button>
        <button
          type="button"
          className="k-btn k-btn--danger k-btn--sm"
          disabled={busy}
          onClick={() => setRejecting((v) => !v)}
        >
          {t("inbox.reject")}
        </button>
      </span>
      {rejecting && (
        <div className="inbox-row__reject" data-testid="inbox-reject-form">
          <label className="k-field__label" htmlFor={`rj-${approval.id}`}>
            {t("inbox.rejectReason")}
          </label>
          <input
            id={`rj-${approval.id}`}
            className="k-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="inbox-reject-reason"
          />
          <button
            type="button"
            className="k-btn k-btn--danger k-btn--sm"
            /* Empty reason is the ONLY disabled cause here, and the label above
               states the field is required — so the reason for the disable is on
               screen rather than inferred. */
            disabled={busy || !reason.trim()}
            onClick={() => reject.mutate({ approvalId: approval.id, reason: reason.trim() })}
            data-testid="inbox-reject-confirm"
          >
            {t("inbox.rejectConfirm")}
          </button>
        </div>
      )}
      {/* THE DEFECT THIS CLOSES. Both grants fired against the server and neither
          rendered its outcome. A four-eyes approval refused for self-approval, an
          expired window or an already-decided request left the button re-enabled
          and the row unchanged — so the approver walked away believing they had
          approved something they had not. Silence on a governed act asserts a
          false success. */}
      <MutationError
        error={latestError(approve, reject)}
        fallback={t("inbox.actionFailed")}
        offline={t("common.offline")}
      />
    </li>
  );
}

// Map an approval's server state onto our status-pill modifiers + a label key.
const SENT_STATUS: Record<ApprovalStatus, { cls: string; key: MessageKey }> = {
  PENDING: { cls: "k-status--pending", key: "inbox.status.PENDING" },
  APPROVED: { cls: "k-status--active", key: "inbox.status.APPROVED" },
  REJECTED: { cls: "k-status--ended", key: "inbox.status.REJECTED" },
  CONSUMED: { cls: "k-status--review", key: "inbox.status.CONSUMED" },
};

function SentRow({ approval }: { approval: Approval }) {
  const { t } = useT();
  const review = approval.review?.app === "console" ? approval.review.path : null;
  const state = SENT_STATUS[approval.status];

  return (
    <li
      className="k-card k-card--pad-sm inbox-row"
      data-testid="sent-row"
      data-action={approval.action}
      data-status={approval.status}
    >
      <div className="inbox-row__body">
        {/* Read-only: the state pill carries the outcome; no act buttons here
            (the requester never grants their own request — approver ≠ requester). */}
        <span className={`k-status ${state.cls}`}>
          <span className="k-status__dot" />
          {t(state.key)}
        </span>
        <span className="inbox-row__meta">
          {approval.action} · {t("inbox.resource")}{" "}
          <code>{approval.resourceId?.slice(0, 8) ?? "—"}</code>
        </span>
      </div>
      <span className="inbox-actions">
        {/* The requester's own list: a link when there is one, silence when not.
            No "cannot review" note here — the requester already knows what they
            asked for; the warning matters for the person being asked to sign. */}
        {review && (
          <Link className="k-btn k-btn--ghost k-btn--sm" to={review} data-testid="sent-review">
            <Icon name="arrow-right" size={16} />
            {t("inbox.review")}
          </Link>
        )}
      </span>
    </li>
  );
}
